// Content tab: what this book is made of, and the way into the editor.
//
// The tab itself is a summary — how many scenes, how long they run, what they
// look like. Everything that changes the content (scenes, pictures, words,
// sounds, tap areas, the book's own settings) is the editor's, which opens
// over the whole screen (SceneEditor).
import React, { useEffect, useMemo, useRef, useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { showNotification } from "aldoo-ra/Notification"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import { RunPipeline, JobStatus, LoadProductMedia, MediaDurations } from "../../../AdminShared/api"
import { errorMessage } from "../../../AdminShared/ui"
import SceneEditor from "./SceneEditor"
import { makeResolver, resolveProduct } from "./resolvePage"
import { computeTiming, formatTime, getPageAudio, getPageImage, imageAttrs } from "../../../Player/layout"

const PIPELINES = [
  { action: "bookscript", label: "PDF → bookscript (pages, texts, images)" },
  { action: "pdf_extract_images", label: "PDF → extract embedded images to the media library" },
]

// what the product's profile plays: an album page's tracks, else a book's pages
const kindOf = (profile) => (profile?.page === "album" ? "music album" : "book")

export default function ContentTab({ editor }) {
  const t = useProductEditorTexts()
  const { pid, product, read, set, reload } = editor
  const profile = read("profile")
  const [job, setJob] = useState(null)
  const [progress, setProgress] = useState(null)
  const [running, setRunning] = useState("")
  const [file, setFile] = useState(null)
  const [media, setMedia] = useState(editor.media || [])
  // the tab opens on what this content is — how many scenes, how much of it
  // there is, what it looks like — with the cutting room one click away
  const [view, setView] = useState("summary")
  const [scene, setScene] = useState(0)
  const timer = useRef(null)

  const [durations, setDurations] = useState({})

  useEffect(() => setMedia(editor.media || []), [editor.media])

  // how long each voice-over is, so scenes have a length here too
  useEffect(() => {
    if (!pid) return
    MediaDurations(pid).then((res) => res?.durations && setDurations(res.durations)).catch(() => {})
  }, [pid])

  // what the reader would get: keys resolved to text and urls, the same way
  // the preview resolves them (resolvePage.js)
  const resolved = useMemo(
    () => resolveProduct(product, makeResolver({ text: editor.text, language: editor.language, defaultLanguage: editor.defaultLanguage, mediaRoot: editor.mediaRoot, durations })),
    [product, editor.text, editor.language, editor.defaultLanguage, editor.mediaRoot, durations]
  )

  const summary = useMemo(() => {
    const pages = resolved?.pages || []
    const { total } = computeTiming(pages)
    return {
      scenes: pages.length,
      runtime: total,
      narrated: pages.filter((page) => getPageAudio(page)).length,
      withMusic: pages.filter((page) => page.music).length,
      withSfx: pages.filter((page) => page.sfx).length,
      triggers: pages.reduce((sum, page) => sum + (page.triggerAreas?.length || 0), 0),
      images: media.filter((item) => item.type === "image").length,
      audio: media.filter((item) => item.type !== "image").length,
      gallery: pages.map((page) => imageAttrs(getPageImage(page))).filter(Boolean),
    }
  }, [resolved, media])

  useEffect(() => {
    if (!job) return
    timer.current = setInterval(async () => {
      const res = await JobStatus(job).catch(() => null)
      setProgress(res)
      if (!res || res.error || res.done || res.finished || res.state === "done" || res.status === "done") {
        clearInterval(timer.current)
        setJob(null)
        setRunning("")
        reload()
      }
    }, 2000)
    return () => clearInterval(timer.current)
  }, [job])

  const run = async (pipeline) => {
    if (!file) return showNotification({ message: t`Pick a PDF first`, className: "bg-admin_error text-white" })
    setRunning(pipeline.action)
    const res = await RunPipeline(pipeline.action, pid, file)
    if (errorMessage(res)) {
      setRunning("")
      return showNotification({ message: errorMessage(res), className: "bg-admin_error text-white" })
    }
    setProgress(res)
    const id = res?.job || res?.jobId || res?.id
    if (id) return setJob(id)
    setRunning("")
    reload()
  }


  // the one thing the editor does not own: turning a PDF into scenes. It
  // rides along in the editor's media browser, where the files it makes land.
  const extras = (
    <div className="flex flex-col gap-4">
      <details className="border border-admin_bordered rounded-lg p-3">
        <summary className="text-sm font-semibold cursor-pointer">{t`Pipelines`}</summary>
        <div className="flex flex-col gap-2 mt-3">
          <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
          {PIPELINES.map((p) => (
            <Button key={p.action} variant="admin_secondary" className="h-[32px] text-xs px-3" onClick={() => run(p)} disabled={!!running || !file}>{running === p.action ? t`Running…` : t(p.label)}</Button>
          ))}
        </div>
        {progress && <pre className="text-xs bg-admin_neutral_1 rounded p-2 overflow-auto max-h-[160px] mt-3 mb-0">{JSON.stringify(progress, null, 2)}</pre>}
      </details>
    </div>
  )

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Typography component="h4" variant="admin_h4" className="my-0 mb-1">{t`Content`}</Typography>
        <p className="text-sm text-admin_text_secondary my-0">{t("What this %s is made of. Everything is edited in the editor.", t(kindOf(profile)))}</p>
      </div>

      {/* what this content is, and the way into the cutting room */}
      <div className="border border-admin_bordered rounded-lg p-6 flex flex-col items-center gap-6" data-testid="content-summary">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 w-full">
          {[
            { label: "Scenes", value: summary.scenes },
            { label: "Runtime", value: formatTime(summary.runtime) },
            { label: "Narrated", value: t("%s of %s", summary.narrated, summary.scenes) },
            { label: "With music", value: summary.withMusic },
            { label: "Sound FX", value: summary.withSfx },
            { label: "Tap areas", value: summary.triggers },
            { label: "Media files", value: t("%s img · %s audio", summary.images, summary.audio) },
          ].map((item) => (
            <div key={item.label} className="rounded-md bg-admin_neutral_1 px-3 py-2">
              <div className="text-xs text-admin_text_secondary">{t(item.label)}</div>
              <div className="text-lg font-semibold tabular-nums text-admin_text truncate">{item.value}</div>
            </div>
          ))}
        </div>

        {/* a look at the book, in the order a reader turns it */}
        {!!summary.gallery.length && (
          <div className="w-full overflow-x-auto" data-testid="content-gallery">
            <div className="flex gap-2 pb-1">
              {summary.gallery.slice(0, 24).map((attrs, index) => (
                <img
                  key={index}
                  src={attrs.src}
                  srcSet={attrs.srcSet}
                  alt=""
                  loading="lazy"
                  className="h-[84px] w-[126px] shrink-0 object-cover rounded-md border border-admin_bordered"
                />
              ))}
              {summary.gallery.length > 24 && (
                <span className="h-[84px] w-[84px] shrink-0 rounded-md bg-admin_neutral_1 flex items-center justify-center text-xs text-admin_text_secondary">
                  +{summary.gallery.length - 24}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col items-center gap-1">
          <Button variant="admin_primary" className="h-[42px] px-6" onClick={() => setView("timeline")} data-testid="content-view-timeline">
            {t`Open the editor`}
          </Button>
          <span className="text-xs text-admin_text_secondary">{t`Scenes, keyframes and the timeline, the size of the screen.`}</span>
        </div>
      </div>

      {view === "timeline" && (
        <SceneEditor editor={{ ...editor, media, durations }} selected={scene} onSelect={setScene} onClose={() => setView("summary")} extras={extras} />
      )}
    </div>
  )
}
